import { ActiveToast } from 'ngx-toastr';
import { Observable } from 'rxjs';

import { Injectable, OnDestroy } from '@angular/core';

import type {
  CadFeatureData,
  CadListUpdatePayload,
  CadFilterConfig,
  CadMarkerLayer,
  CameraGeoCount,
  CadActionType,
  CadAttributeType,
} from '@app/dashboard/map/services/map-cad/types';
import type { SnEsriQueryLayer, SnLayer, SnLayerSetting } from '@lib/api/entities';
import type { SnCad } from '@lib/cad';

import { FsMapCadServiceProvider, type ISnCadToastData, type IFsMap } from '@lib/geo/core';

@Injectable()
export class MapCadFacadeService<TMap extends IFsMap = IFsMap>
  extends FsMapCadServiceProvider<TMap>
  implements OnDestroy
{
  constructor(private readonly provider: FsMapCadServiceProvider<TMap>) {
    super();
  }

  public ngOnDestroy(): void {
    this.provider.ngOnDestroy();
  }

  public get cadFields$(): Observable<{ fields: any[]; layer: SnEsriQueryLayer }> {
    return this.provider.cadFields$;
  }

  public get cadList$(): Observable<SnCad[]> {
    return this.provider.cadList$;
  }

  public get cadName$(): Observable<string> {
    return this.provider.cadName$;
  }

  public get cadActive$(): Observable<string[]> {
    return this.provider.cadActive$;
  }

  public get cadToast$(): Observable<ISnCadToastData> {
    return this.provider.cadToast$;
  }

  public get layerSettings(): any | SnLayerSetting {
    return this.provider.layerSettings;
  }

  public get addCad$(): Observable<SnCad> {
    return this.provider.addCad$;
  }

  public get cameraCounts$(): Observable<any> {
    return this.provider.cameraCounts$;
  }

  public get updateWebGlMarkers$(): Observable<any> {
    return this.provider.updateWebGlMarkers$;
  }

  public get initAndExpandChange$(): Observable<any> {
    return this.provider.initAndExpandChange$;
  }

  public get layerAdded$(): Observable<any> {
    return this.provider.layerAdded$;
  }

  public get layerRemoved$(): Observable<any> {
    return this.provider.layerRemoved$;
  }

  public get skydioUpdated$(): Observable<any> {
    return this.provider.skydioUpdated$;
  }

  public get activeCad(): SnCad {
    return this.provider.activeCad;
  }

  public set activeCad(value: SnCad) {
    this.provider.activeCad = value;
  }

  public get cadActive(): string {
    return this.provider.cadActive;
  }

  public set cadActive(value: string) {
    this.provider.cadActive = value;
  }

  public get currentSize(): number {
    return this.provider.currentSize;
  }

  public set currentSize(value: number) {
    this.provider.currentSize = value;
  }

  public get cadFilters(): any {
    return this.provider.cadFilters;
  }

  public set cadFilters(value: any) {
    this.provider.cadFilters = value;
  }

  public get adminCadFilters(): any {
    return this.provider.adminCadFilters;
  }

  public set adminCadFilters(value: any) {
    this.provider.adminCadFilters = value;
  }

  public get _fields(): string[] {
    return this.provider._fields;
  }

  public set _fields(value: string[]) {
    this.provider._fields = value;
  }

  public get rapidSOSEnabled(): boolean {
    return this.provider.rapidSOSEnabled;
  }

  public set rapidSOSEnabled(value: boolean) {
    this.provider.rapidSOSEnabled = value;
  }

  public get mobilePhone(): boolean {
    return this.provider.mobilePhone;
  }

  public set mobilePhone(value: boolean) {
    this.provider.mobilePhone = value;
  }

  public get cadMarkers(): any[] {
    return this.provider.cadMarkers;
  }

  public set cadMarkers(value: any[]) {
    this.provider.cadMarkers = value;
  }

  public get webGlMarkers(): any {
    return this.provider.webGlMarkers;
  }

  public set webGlMarkers(value: any) {
    this.provider.webGlMarkers = value;
  }

  public get activeLayers(): SnLayer[] {
    return this.provider.activeLayers;
  }

  public set activeLayers(value: SnLayer[]) {
    this.provider.activeLayers = value;
  }

  public get cadDrawerLayerNames(): string {
    return this.provider.cadDrawerLayerNames;
  }

  public set cadDrawerLayerNames(value: string) {
    this.provider.cadDrawerLayerNames = value;
  }

  public get combinedCad(): Map<any, any> {
    return this.provider.combinedCad;
  }

  public set combinedCad(value: Map<any, any>) {
    this.provider.combinedCad = value;
  }

  public get isExpanded(): boolean {
    return this.provider.isExpanded;
  }

  public set isExpanded(value: boolean) {
    this.provider.isExpanded = value;
  }

  public get cadCameras(): Map<string, number> {
    return this.provider.cadCameras;
  }

  public set cadCameras(value: Map<string, number>) {
    this.provider.cadCameras = value;
  }

  public initAndExpandChange(): void {
    this.provider.initAndExpandChange();
  }

  public enableRapidSOS(): void {
    this.provider.enableRapidSOS();
  }

  public disableRapidSOS(): void {
    this.provider.disableRapidSOS();
  }

  public updateCadList(layer: any, payload: any): void {
    this.provider.updateCadList(layer, payload);
  }

  public setActive(active: string): void {
    this.provider.setActive(active);
  }

  public addActiveLayer(layer: SnLayer): void {
    this.provider.addActiveLayer(layer);
  }

  public setAvl(mapLayer: any): void {
    this.provider.setAvl(mapLayer);
  }

  public setFields(fields: any, layer?: SnEsriQueryLayer): void {
    this.provider.setFields(fields, layer);
  }

  public removeIdleCad(seconds: number): void {
    this.provider.removeIdleCad(seconds);
  }

  public updateCadById(id: string | Number, item: CadFeatureData, translate?: boolean): void {
    this.provider.updateCadById(id, item, translate);
  }

  public removeCadById(id: string | Number): void {
    this.provider.removeCadById(id);
  }

  public initPlayback(data: CadFeatureData, attributeType: CadAttributeType): boolean {
    return this.provider.initPlayback(data, attributeType);
  }

  public initCadToast(
    data: CadFeatureData,
    layer?: SnLayer,
    attributeType?: CadAttributeType,
  ): ActiveToast<any> {
    return this.provider.initCadToast(data, layer, attributeType);
  }

  public initCadSound(data?: CadFeatureData, attributeType?: CadAttributeType): void {
    this.provider.initCadSound(data, attributeType);
  }

  public showDynamicCadToast(cad: SnCad): void {
    this.provider.showDynamicCadToast(cad);
  }

  public getNearbyCameras(data: SnCad, radius: number): void {
    this.provider.getNearbyCameras(data, radius);
  }

  public closeCadToasts(): void {
    this.provider.closeCadToasts();
  }

  public checkPhoneNumber(phoneNumber: string): void {
    this.provider.checkPhoneNumber(phoneNumber);
  }

  public checkDispatch(): void {
    this.provider.checkDispatch();
  }

  public updateWebGlMarkers(data: Record<string, any>): void {
    this.provider.updateWebGlMarkers(data);
  }

  public removeActiveLayer(layer: SnLayer): void {
    this.provider.removeActiveLayer(layer);
  }

  public dockCamera(feature: CadFeatureData): void {
    this.provider.dockCamera(feature);
  }

  public onFeatureMouseOver(feature: CadFeatureData): void {
    this.provider.onFeatureMouseOver(feature);
  }

  public onFeatureMouseOut(feature: CadFeatureData): void {
    this.provider.onFeatureMouseOut(feature);
  }

  public skydioUpdate(feature: CadFeatureData): void {
    this.provider.skydioUpdate(feature);
  }

  public coreMobileUpdate(feature: CadFeatureData): void {
    this.provider.coreMobileUpdate(feature);
  }
}
